import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export interface StandardsReleaseSummary {
  release_id: string
  framework_id: string
  body: string
  version: string
  effective_date: string
  status: 'draft' | 'validated' | 'active' | 'retired'
  review_requested_at: string | null
  retired_at: string | null
  notes: string | null
}

export interface StandardsReleaseDetail {
  release: StandardsReleaseSummary & {
    educator_signoff: Record<string, unknown> | null
    clinical_reviewer_signoff: Record<string, unknown> | null
    publish_approval: Record<string, unknown> | null
    created_at: string
    updated_at: string
  }
  provenance: Record<string, unknown> | null
}

export function canManageStandardsRelease(isSuperadmin: boolean): boolean {
  return isSuperadmin
}

export function isImmutableReleaseField(fieldName: string): boolean {
  return ['release_id', 'framework_id', 'body', 'version', 'effective_date', 'status'].includes(fieldName)
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function toSummary(value: unknown): StandardsReleaseSummary {
  if (!asRecord(value)) throw new Error('Malformed standards release in server response')
  if (typeof value.release_id !== 'string') throw new Error('Malformed standards release in server response')

  return {
    release_id: value.release_id,
    framework_id: typeof value.framework_id === 'string' ? value.framework_id : '',
    body: typeof value.body === 'string' ? value.body : '',
    version: typeof value.version === 'string' ? value.version : '',
    effective_date: typeof value.effective_date === 'string' ? value.effective_date : '',
    status:
      value.status === 'validated' || value.status === 'active' || value.status === 'retired'
        ? value.status
        : 'draft',
    review_requested_at: typeof value.review_requested_at === 'string' ? value.review_requested_at : null,
    retired_at: typeof value.retired_at === 'string' ? value.retired_at : null,
    notes: typeof value.notes === 'string' ? value.notes : null,
  }
}

export async function getStandardsReleases(): Promise<StandardsReleaseSummary[]> {
  const { data, error } = await invokeFunction<{ releases: unknown }>('standards-releases', {
    method: 'GET',
    path: '/releases',
  })

  if (error) throw toApiRequestError(error, 'Failed to load standards releases')
  if (!data || !Array.isArray(data.releases)) throw new Error('Malformed standards releases response from server')
  return data.releases.map(toSummary)
}

export async function getStandardsRelease(releaseId: string): Promise<StandardsReleaseDetail> {
  const { data, error } = await invokeFunction<unknown>('standards-releases', {
    method: 'GET',
    path: `/releases/${releaseId}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load standards release')
  if (!asRecord(data)) throw new Error('Malformed standards release response from server')
  if (!asRecord(data.release)) throw new Error('Malformed standards release response from server')

  return {
    release: {
      ...toSummary(data.release),
      educator_signoff: asRecord(data.release.educator_signoff) ? data.release.educator_signoff : null,
      clinical_reviewer_signoff: asRecord(data.release.clinical_reviewer_signoff) ? data.release.clinical_reviewer_signoff : null,
      publish_approval: asRecord(data.release.publish_approval) ? data.release.publish_approval : null,
      created_at: typeof data.release.created_at === 'string' ? data.release.created_at : new Date(0).toISOString(),
      updated_at: typeof data.release.updated_at === 'string' ? data.release.updated_at : new Date(0).toISOString(),
    },
    provenance: asRecord(data.provenance) ? data.provenance : null,
  }
}

export async function requestReleaseReview(releaseId: string): Promise<void> {
  const { error } = await invokeFunction<unknown>('standards-releases', {
    method: 'POST',
    path: `/releases/${releaseId}/request-review`,
    body: {},
  })
  if (error) throw toApiRequestError(error, 'Failed to request release review')
}

export async function getReleaseDiffPreview(releaseId: string): Promise<{ diff_signature: string }> {
  const { data, error } = await invokeFunction<{ diff_preview: { diff_signature: string } }>('standards-releases', {
    method: 'GET',
    path: `/releases/${releaseId}/diff-preview`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load release diff preview')
  if (!data || !data.diff_preview || typeof data.diff_preview.diff_signature !== 'string') {
    throw new Error('Malformed diff preview response from server')
  }
  return data.diff_preview
}

export async function approvePublish(releaseId: string, approverName: string): Promise<void> {
  const { error } = await invokeFunction<unknown>('standards-releases', {
    method: 'POST',
    path: `/releases/${releaseId}/approve-publish`,
    body: { approver_name: approverName },
  })
  if (error) throw toApiRequestError(error, 'Failed to approve release for publish')
}

export async function publishRelease(releaseId: string, approverName: string, diffSignature: string): Promise<void> {
  const { error } = await invokeFunction<unknown>('standards-releases', {
    method: 'POST',
    path: `/releases/${releaseId}/publish`,
    body: { approver_name: approverName, diff_signature: diffSignature },
  })
  if (error) throw toApiRequestError(error, 'Failed to publish release')
}

export async function publishWithSupersession(release: StandardsReleaseSummary, approverName: string): Promise<void> {
  if (release.status === 'draft' && !release.review_requested_at) {
    await requestReleaseReview(release.release_id)
  }
  const diffPreview = await getReleaseDiffPreview(release.release_id)
  await approvePublish(release.release_id, approverName)
  await publishRelease(release.release_id, approverName, diffPreview.diff_signature)
}
